import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  useDisclosure,
  Flex,
  Badge,
  Radio,
  RadioGroup,
  Stack,
  Progress,
  Icon,
  useToast,
  Divider,
  HStack,
} from "@chakra-ui/react";
import { CheckCircleIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FiPlay, FiHeadphones, FiCheckCircle, FiBarChart2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { LoginWithSmsModal } from "../components/LoginWithSmsModal";
import { useAuth } from "../contexts/AuthContext";

// Mock investment options
const INVESTMENT_OPTIONS = [
  {
    id: "property",
    title: "Long-term Property Growth",
    description: "A 5-year property investment strategy focusing on capital growth in high-potential areas.",
    votes: 37
  },
  {
    id: "business",
    title: "Business Investment",
    description: "Investing in small to medium businesses with sustainable practices, like regenerative agriculture.",
    votes: 28
  },
  {
    id: "token",
    title: "Tokenized Real Estate",
    description: "Fractional ownership through blockchain tokens, providing liquidity and smaller investment threshold.",
    votes: 42
  },
  {
    id: "rental",
    title: "Income-Focused Strategy",
    description: "Focus on property with strong rental yields and consistent income generation.",
    votes: 19
  }
];

const P1 = () => {
  const { isOpen: isSmsModalOpen, onOpen: onSmsModalOpen, onClose: onSmsModalClose } = useDisclosure();
  const { isAuthenticated, user } = useAuth();
  const [selectedOption, setSelectedOption] = useState("");
  const [hasVoted, setHasVoted] = useState(false);
  const [options, setOptions] = useState(INVESTMENT_OPTIONS);
  const toast = useToast();
  const navigate = useNavigate();

  // Check if user is authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      onSmsModalOpen();
    }
    window.scrollTo(0, 0);
  }, [isAuthenticated, onSmsModalOpen]);

  // Calculate total votes for percentage calculation
  const totalVotes = options.reduce((sum, option) => sum + option.votes, 0);

  const handleLoginSuccess = () => {
    onSmsModalClose();
  };

  const handleVote = () => {
    if (!selectedOption) {
      toast({
        title: "Please select an option",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    // Update the votes for the selected option
    const updatedOptions = options.map(option => 
      option.id === selectedOption 
        ? { ...option, votes: option.votes + 1 } 
        : option
    );

    setOptions(updatedOptions);
    setHasVoted(true);

    toast({
      title: "Vote submitted!",
      description: "Thank you for your input on our investment strategy.",
      status: "success",
      duration: 3000,
    });

    // Redirect to results page after a short delay
    setTimeout(() => {
      navigate("/investment-results");
    }, 2000);
  };

  return (
    <Box bg="#f7f7f7" minH="100vh">
      <NavbarWithCallToAction />
      
      {/* Hero Section */}
      <Box 
        bg="linear-gradient(135deg, #2c5282 0%, #39a169 100%)" 
        py={16} 
        color="white"
      >
        <Container maxW="container.md">
          <VStack spacing={6} textAlign="center">
            <Badge
              px={4}
              py={2}
              bg="white"
              color="#2c5282"
              borderRadius="full"
              fontSize="md"
              fontWeight="600"
            >
              EPISODE 1
            </Badge>
            
            <Heading size="2xl" lineHeight="1.2">
              New Ways to Co-Invest in Property with Blockchain Technology
            </Heading>
            
            <Text fontSize="xl" maxW="700px">
              Join Sun and Tom as they discuss innovative approaches to property investment
              using blockchain technology and collaborative decision-making
            </Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.md" py={16}>
        <VStack spacing={12}>
          {/* Podcast Episode Section */}
          <Box 
            w="full" 
            bg="white" 
            p={8} 
            borderRadius="xl" 
            boxShadow="md"
            border="1px solid"
            borderColor="gray.100"
          >
            <VStack spacing={6} align="start" w="full">
              <Flex 
                w="full" 
                justify="center" 
                mb={4}
                bg="gray.100"
                minH="250px"
                borderRadius="lg"
                align="center"
                position="relative"
                overflow="hidden"
              >
                {/* Placeholder for podcast player */}
                <Text color="gray.500">Podcast Player Placeholder</Text>
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  bg="white"
                  w="80px"
                  h="80px"
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  boxShadow="lg"
                  opacity="0.9"
                  _hover={{
                    opacity: 1,
                    transform: "translate(-50%, -50%) scale(1.05)"
                  }}
                  transition="all 0.2s"
                >
                  <Icon as={FiPlay} w={10} h={10} color="#39a169" />
                </Box>
              </Flex>
              
              <Heading size="lg">Episode 1: Collaborative Decision-Making in Property Investment</Heading>
              
              <Text fontSize="md" color="gray.600">
                In this first episode, we explore how blockchain technology is revolutionizing property investment by enabling transparent,
                democratic decision-making among investors. Learn how this new model can provide better returns while distributing risk.
              </Text>

              <Divider my={4} />

              <Heading size="md">Episode Highlights:</Heading>
              <VStack align="start" spacing={2} w="full">
                <HStack align="start" spacing={3}>
                  <Icon as={FiCheckCircle} color="green.500" mt={1} />
                  <Text>Introduction to collaborative property investment models</Text>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={FiCheckCircle} color="green.500" mt={1} />
                  <Text>How blockchain provides transparency and trust in group investments</Text>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={FiCheckCircle} color="green.500" mt={1} />
                  <Text>The role of Smart Contracts in automating decision processes</Text>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={FiCheckCircle} color="green.500" mt={1} />
                  <Text>Using Self-Managed Super Funds (SMSF) for property investment</Text>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={FiCheckCircle} color="green.500" mt={1} />
                  <Text>Case studies of successful co-owned property investments</Text>
                </HStack>
              </VStack>
            </VStack>
          </Box>

          {/* Voting Section */}
          <Box 
            w="full" 
            bg="white" 
            p={8} 
            borderRadius="xl" 
            boxShadow="md"
            border="1px solid"
            borderColor="gray.100"
          >
            <VStack spacing={6} align="start" w="full">
              <Flex 
                w="full" 
                justify="space-between" 
                align="center"
                wrap="wrap"
                gap={4}
              >
                <Heading size="lg">Cast Your Vote</Heading>
                <Badge
                  px={3}
                  py={1}
                  bg="green.100"
                  color="green.700"
                  borderRadius="full"
                  fontSize="sm"
                >
                  <Flex align="center" gap={1}>
                    <Icon as={FiBarChart2} />
                    <Text>{totalVotes} votes so far</Text>
                  </Flex>
                </Badge>
              </Flex>
              
              <Text fontSize="md" color="gray.600">
                What investment strategy would you prefer to participate in? Your vote will help shape the focus of our future episodes
                and investment opportunities.
              </Text>

              <Box w="full" px={2} py={4}>
                <RadioGroup onChange={setSelectedOption} value={selectedOption} isDisabled={hasVoted}>
                  <Stack spacing={5} direction="column">
                    {options.map(option => (
                      <Box 
                        key={option.id}
                        p={4}
                        borderRadius="md"
                        border="1px solid"
                        borderColor={selectedOption === option.id ? "green.300" : "gray.200"}
                        bg={selectedOption === option.id ? "green.50" : "white"}
                        transition="all 0.2s"
                        _hover={{
                          borderColor: "green.300",
                          bg: "green.50"
                        }}
                      >
                        <Flex direction="column" w="full">
                          <Radio value={option.id} colorScheme="green" mb={2}>
                            <Text fontWeight="bold">{option.title}</Text>
                          </Radio>
                          <Text fontSize="sm" color="gray.600" ml={6}>{option.description}</Text>
                          
                          {hasVoted && (
                            <Box mt={3} w="full">
                              <Flex justify="space-between" mb={1}>
                                <Text fontSize="xs" color="gray.500">{option.votes} votes</Text>
                                <Text fontSize="xs" color="gray.500">
                                  {Math.round((option.votes / totalVotes) * 100)}%
                                </Text>
                              </Flex>
                              <Progress 
                                value={(option.votes / totalVotes) * 100} 
                                colorScheme="green" 
                                size="sm" 
                                borderRadius="full" 
                              />
                            </Box>
                          )}
                        </Flex>
                      </Box>
                    ))}
                  </Stack>
                </RadioGroup>
              </Box>

              <Box w="full" mt={4}>
                <Button
                  colorScheme="green"
                  size="lg"
                  width="full"
                  onClick={handleVote}
                  isDisabled={hasVoted || !selectedOption}
                  rightIcon={<ChevronRightIcon />}
                >
                  {hasVoted ? "Thanks for voting!" : "Submit Your Vote"}
                </Button>
                {hasVoted && (
                  <Text fontSize="sm" color="gray.500" textAlign="center" mt={2}>
                    Redirecting to results page...
                  </Text>
                )}
              </Box>
            </VStack>
          </Box>

          {/* Call to Action */}
          <Box
            w="full"
            bg="linear-gradient(135deg, rgba(51, 87, 134, 0.05) 0%, rgba(57, 161, 105, 0.1) 100%)"
            p={10}
            borderRadius="xl"
            border="1px solid"
            borderColor="rgba(57, 161, 105, 0.2)"
            textAlign="center"
          >
            <VStack spacing={6}>
              <Icon as={FiHeadphones} w={12} h={12} color="#39a169" />
              <Heading size="lg">Join the Life Essentials Club for Just $1</Heading>
              <Text fontSize="lg" maxW="600px">
                Become a member today to gain exclusive access to future episodes, investment opportunities,
                and the ability to participate in our blockchain-powered property investment system.
              </Text>
              <Button
                size="lg"
                colorScheme="green"
                onClick={() => navigate("/join")}
                px={8}
                py={7}
                fontSize="lg"
                boxShadow="md"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg"
                }}
              >
                Join for $1
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <LoginWithSmsModal 
        isOpen={isSmsModalOpen} 
        onClose={onSmsModalClose}
        onLoginSuccess={handleLoginSuccess}
      />
      
      <FooterWithFourColumns />
    </Box>
  );
};

export default P1; 